import '../../App.css';

import { useEffect, useState, useCallback, useRef } from 'react';
import DailyIframe, { DailyCall, DailyEvent } from '@daily-co/daily-js';
import { DailyAudio, DailyProvider } from '@daily-co/daily-react';

import api from '../../lib/api/Call';
import { Persona } from "../../lib/api/Persona";
import { roomUrlFromPageUrl, pageUrlFromRoomUrl } from '../../lib/helpers/urlFormatter';

import NewHomeScreen from '../../components/NewHomeScreen/NewHomeScreen';
import CallScreen from '../../components/CallScreen/CallScreen';
import HairCheck from '../../components/HairCheck/HairCheck';
import { useUserInfoContext } from "../../lib/contexts/UserInfoContext";
import { useChatSessionsContext } from "../../lib/contexts/ChatSessionsContext";
import { isUserAuthorized, canAccessCall } from '@common/domain/auth';
import { useNavigate, useLocation } from 'react-router-dom';

/* We decide what UI to show to users based on the state of the app, which is dependent on the state of the call object. */
const STATE_IDLE = 'STATE_IDLE';
const STATE_CREATING = 'STATE_CREATING';
const STATE_JOINING = 'STATE_JOINING';
const STATE_JOINED = 'STATE_JOINED';
const STATE_LEAVING = 'STATE_LEAVING';
const STATE_ERROR = 'STATE_ERROR';
const STATE_HAIRCHECK = 'STATE_HAIRCHECK';

const CREATE_CONVERSATION_BUDGET_MS = 30000;
const DEVICE_PREP_BUDGET_MS = 15000;
const JOIN_CONVERSATION_BUDGET_MS = 45000;

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, message: string) {
  return new Promise<T>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      reject(new Error(message));
    }, timeoutMs);

    promise
      .then((result) => {
        window.clearTimeout(timeoutId);
        resolve(result);
      })
      .catch((error) => {
        window.clearTimeout(timeoutId);
        reject(error);
      });
  });
}

export default function Call() {
  const navigate = useNavigate();
  const location = useLocation();
  const [appState, setAppState] = useState<string>(STATE_IDLE);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [roomUrl, setRoomUrl] = useState<string | undefined>(undefined);
  const [callObject, setCallObject] = useState<DailyCall | null>(null);
  const [apiError, setApiError] = useState<boolean>(false);
  const [apiErrorMessage, setApiErrorMessage] = useState<string | null>(null);
  const { userInfo } = useUserInfoContext();
  const {
    persona: contextPersona,
    selectedChatSession,
    updatePersona: setContextPersona,
  } = useChatSessionsContext();
  const [persona, setPersona] = useState<Persona | null>(null);

  const appStateRef = useRef(appState);
  const conversationIdRef = useRef(conversationId);
  const userInfoRef = useRef(userInfo);
  const callObjectRef = useRef(callObject);

  useEffect(() => { appStateRef.current = appState; }, [appState]);
  useEffect(() => { conversationIdRef.current = conversationId; }, [conversationId]);
  useEffect(() => { userInfoRef.current = userInfo; }, [userInfo]);
  useEffect(() => { callObjectRef.current = callObject; }, [callObject]);

  const resetDemoState = useCallback(async () => {
    const existingCall = callObjectRef.current ?? DailyIframe.getCallInstance();
    if (existingCall) {
      try {
        await existingCall.destroy();
      } catch (error) {
        console.warn('Failed to destroy call object during reset', error);
      }
    }

    setConversationId(null);
    setRoomUrl(undefined);
    setCallObject(null);
    setApiError(false);
    setApiErrorMessage(null);
    setAppState(STATE_IDLE);
  }, []);

  const handleRecoverableError = useCallback(async (message: string) => {
    console.error('[CallFlow]', message);
    setApiError(true);
    setApiErrorMessage(message);
    setAppState(STATE_ERROR);
    const existingCall = callObjectRef.current ?? DailyIframe.getCallInstance();
    if (existingCall) {
      try {
        await existingCall.destroy();
      } catch (error) {
        console.warn('Failed to destroy call object after error', error);
      }
    }
    setCallObject(null);
  }, []);

  /**
   * Create a new call room. This function will return the newly created room URL.
   * We'll need this URL when pre-authorizing (https://docs.daily.co/reference/rn-daily-js/instance-methods/pre-auth)
   * or joining (https://docs.daily.co/reference/rn-daily-js/instance-methods/join) a call.
   */
  const createCall = useCallback((
    selectedPersona: Persona | undefined,
    chatId?: string,
    conversationalContext?: Array<{ role: 'user' | 'assistant'; content: string }>
  ) => {
    setAppState(STATE_CREATING);
    setApiError(false);
    setApiErrorMessage(null);
    const p = selectedPersona || persona;
    if (p) {
      setPersona(p);
    }
    return withTimeout(
      api.startConversation(userInfo, p || undefined, chatId, conversationalContext),
      CREATE_CONVERSATION_BUDGET_MS,
      'Starting the conversation took too long. Please try again.'
    )
      .then((room) => {
        if (!room?.conversation_id || !room?.conversation_url) {
          throw new Error('The call service returned an incomplete response.');
        }
        console.log('Created room', room);
        console.log('User info:', userInfo);        
        setConversationId(room.conversation_id);
        setRoomUrl(room.conversation_url);
        return room.conversation_url;
      })
      .catch((error) => {
        console.error('Error creating room', error);
        const message = error instanceof Error
          ? error.message
          : 'We could not start the call. Please try again.';
        handleRecoverableError(message);
        throw error;
      });
  }, [userInfo, persona, handleRecoverableError]);

  /**
   * Initialize the hair check screen without an active Tavus room.
   */
  const enterHairCheck = useCallback(async (selectedPersona: Persona) => {
    setApiError(false);
    setApiErrorMessage(null);
    try {
      const existingCall = DailyIframe.getCallInstance();
      if (existingCall) {
        await existingCall.destroy();
      }
      const newCallObject = DailyIframe.createCallObject();
      if (contextPersona?.id !== selectedPersona.id) {
        setContextPersona(selectedPersona);
      }
      setPersona(selectedPersona);
      setCallObject(newCallObject);
      setAppState(STATE_HAIRCHECK);
      await withTimeout(
        newCallObject.startCamera(),
        DEVICE_PREP_BUDGET_MS,
        'Camera setup took too long. Please check permissions and retry.'
      );
    } catch (error: any) {
      await handleRecoverableError(
        error?.message || 'We could not prepare camera and microphone access.'
      );
    }
  }, [handleRecoverableError, contextPersona, setContextPersona]);

  // Sync local persona with context persona
  useEffect(() => {
    if (contextPersona) {
      setPersona(contextPersona);
    } else {
      setPersona(null);
      if (appState === STATE_HAIRCHECK) {
        resetDemoState();
      }
    }
  }, [contextPersona, appState, resetDemoState]);

  // Enter video haircheck only when arriving with explicit "start video call" intent
  // (e.g. clicking the Video Call button in /text-chat). Picking a companion from the
  // home grid routes to /text-chat directly via startChat.
  useEffect(() => {
    const wantsVideo = (location.state as { startVideoCall?: boolean } | null)?.startVideoCall;
    if (appState === STATE_IDLE && wantsVideo && contextPersona) {
      enterHairCheck(contextPersona);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [appState, selectedChatSession, contextPersona, enterHairCheck, location, navigate]);

  /**
   * Pick a companion from the home grid → drop straight into text chat.
   */
  const startChat = useCallback((selectedPersona: Persona) => {
    if (contextPersona?.id !== selectedPersona.id) {
      setContextPersona(selectedPersona);
    }
    navigate('/text-chat');
  }, [contextPersona, setContextPersona, navigate]);

  /**
   * We've created a room, so let's start the hair check. We won't be joining the call yet.
   */
  const startHairCheck = useCallback(async (url: string) => {
    setApiError(false);
    setApiErrorMessage(null);
    try {
      const existingCall = DailyIframe.getCallInstance();
      if (existingCall) {
        await existingCall.destroy();
      }
      const newCallObject = DailyIframe.createCallObject();
      setRoomUrl(url);
      setCallObject(newCallObject);
      setAppState(STATE_HAIRCHECK);
      await withTimeout(
        newCallObject.preAuth({ url }),
        DEVICE_PREP_BUDGET_MS,
        'Pre-authorization took too long. Please retry.'
      );
      await withTimeout(
        newCallObject.startCamera(),
        DEVICE_PREP_BUDGET_MS,
        'Camera setup took too long. Please check permissions and retry.'
      );
    } catch (error: any) {
      await handleRecoverableError(
        error?.message || 'We could not prepare the call preview.'
      );
    }
  }, [handleRecoverableError]);

  /**
   * Once we pass the hair check, we can actually join the call.
   * We'll pass the username entered during Haircheck to .join().
   */
  const joinCall = useCallback((userName: string, url?: string) => {
    const joinUrl = url || roomUrl;
    if (!joinUrl) {
      console.error("Cannot join call: No room URL available.");
      setApiError(true);
      setApiErrorMessage("No room URL was available. Please restart the call.");
      setAppState(STATE_ERROR);
      return;
    }
    if (!callObject) {
      setApiError(true);
      setApiErrorMessage("The call preview is not ready yet. Please retry.");
      setAppState(STATE_ERROR);
      return;
    }
    setApiError(false);
    setApiErrorMessage(null);
    setAppState(STATE_JOINING);
    callObject.join({ url: joinUrl, userName }).catch((error) => {
      console.error('Failed to join call', error);
      setApiError(true);
      setApiErrorMessage('Joining the call failed. Please try again.');
      setAppState(STATE_ERROR);
    });
  }, [callObject, roomUrl]);

  /**
   * Start leaving the current call.
   */
  const startLeavingCall = useCallback(() => {
    if (!callObject) return;
    // If we're in the error state, we've already "left", so just clean up
    if (appState === STATE_ERROR) {
      callObject.destroy().then(() => {
        setRoomUrl(undefined);
        setCallObject(null);
        setAppState(STATE_IDLE);
      });
    } else {
      /* This will trigger a `left-meeting` event, which in turn will trigger
      the full clean-up as seen in handleNewMeetingState() below. */
      setAppState(STATE_LEAVING);
      callObject.leave();
      if (conversationId) {
        api.endConversation(userInfo, conversationId)
      }
    }
  }, [callObject, appState, conversationId, userInfo]);

  /**
   * Navigation guards and cleanup
   */
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (conversationIdRef.current && [STATE_CREATING, STATE_JOINING, STATE_JOINED].includes(appStateRef.current)) {
        e.preventDefault();
        e.returnValue = 'Call is still active. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);

      const state = appStateRef.current;
      const cid = conversationIdRef.current;
      const co = callObjectRef.current;
      const ui = userInfoRef.current;

      if (co) {
        if (cid && [STATE_CREATING, STATE_JOINING, STATE_JOINED].includes(state)) {
          api.endConversation(ui, cid);
        }
        co.destroy();
      }
    };
  }, []);

  /**
   * If a room's already specified in the page's URL when the component mounts,
   * join the room.
   */
  useEffect(() => {
    const url = roomUrlFromPageUrl();
    if (url) {
      startHairCheck(url);
    }
  }, [startHairCheck]);

  /**
   * Update the page's URL to reflect the active call when roomUrl changes.
   */
  useEffect(() => {
    const pageUrl = pageUrlFromRoomUrl(roomUrl ?? "");
    if (pageUrl === window.location.href) return;
    window.history.replaceState(null, "", pageUrl);
  }, [roomUrl]);

  /**
   * Check for authorization
   */
  useEffect(() => {
    if (userInfo) {
      if (!isUserAuthorized(userInfo.groups)) {
        navigate("/unauthorized");
      } else if (!canAccessCall(userInfo.groups)) {
        navigate("/text-chat");
      }
    }
  }, [userInfo, navigate]);

  /**
   * Update app state based on reported meeting state changes.
   *
   * NOTE: Here we're showing how to completely clean up a call with destroy().
   * This isn't strictly necessary between join()s, but is good practice when
   * you know you'll be done with the call object for a while, and you're no
   * longer listening to its events.
   */
  useEffect(() => {
    if (!callObject) return;

    const events = ['joined-meeting', 'left-meeting', 'error', 'camera-error'];

    function handleNewMeetingState() {
      switch (callObject?.meetingState()) {
        case 'joined-meeting':
          setAppState(STATE_JOINED);
          break;
        case 'left-meeting':
          callObject.destroy().then(() => {
            setRoomUrl(undefined);
            setCallObject(null);
            setAppState(STATE_IDLE);
          });
          break;
        case 'error':
          setApiError(true);
          setApiErrorMessage('The call disconnected before it could fully connect.');
          setAppState(STATE_ERROR);
          break;
        default:
          break;
      }
    }

    // Use initial state
    handleNewMeetingState();

    /*
     * Listen for changes in state.
     * We can't use the useDailyEvent hook (https://docs.daily.co/reference/daily-react/use-daily-event) for this
     * because right now, we're not inside a <DailyProvider/> (https://docs.daily.co/reference/daily-react/daily-provider)
     * context yet. We can't access the call object via daily-react just yet, but we will later in Call.js and HairCheck.js!
     */
    events.forEach((event) => callObject.on(event as DailyEvent, handleNewMeetingState));

    // Stop listening for changes in state
    return () => {
      events.forEach((event) => callObject.off(event as DailyEvent, handleNewMeetingState));
    };
  }, [callObject]);

  useEffect(() => {
    if (appState !== STATE_JOINING) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      if (appStateRef.current === STATE_JOINING) {
        handleRecoverableError('Joining the call took too long. Please retry.');
      }
    }, JOIN_CONVERSATION_BUDGET_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [appState, handleRecoverableError]);

  /**
   * Show the call UI if we're either joining, already joined, or have encountered
   * an error that is _not_ a room API error.
   */
  const showCall = !apiError && [STATE_JOINING, STATE_JOINED, STATE_ERROR].includes(appState);

  /* When there's no problems creating the room and startHairCheck() has been successfully called,
   * we can show the hair check UI. */
  const showHairCheck = !apiError && [STATE_HAIRCHECK, STATE_CREATING].includes(appState);

  const renderApp = () => {
    if (apiError || appState === STATE_ERROR) {
      return (
        <div className="api-error" style={{ padding: '48px 24px', maxWidth: 640, margin: '0 auto' }}>
          <h1>Call Recovery</h1>
          <p>
            {apiErrorMessage || 'The call could not continue.'}
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button type="button" onClick={() => resetDemoState()}>
              Return to Home
            </button>
            <button
              type="button"
              onClick={() => {
                if (roomUrl) {
                  startHairCheck(roomUrl);
                } else {
                  resetDemoState();
                }
              }}
            >
              Retry
            </button>
          </div>
        </div>
      );
    }

    if ((showHairCheck || showCall) && persona) {
      return (
        <DailyProvider callObject={callObject}>
          {showHairCheck ? (
            // No API errors? Let's check our hair then.
            <HairCheck
              joinCall={joinCall}
              cancelCall={startLeavingCall}
              persona={persona}
              createCall={createCall}
              isCreating={appState === STATE_CREATING}
            />
          ) : (
            // No API errors, we passed the hair check, and we've joined the call? Then show the call.
            <>
              {conversationId && (
                <CallScreen conversationId={conversationId} leaveCall={startLeavingCall} persona={persona} />
              )}
              <DailyAudio />
            </>
          )}
        </DailyProvider>
      );
    }

    // The default view is the HomeScreen, from where we start the demo.
    return <NewHomeScreen createCall={createCall} startChat={startChat} />;
  }

  return renderApp()
}
